import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import { usePermissions } from '@/contexts/auth';
import { useCage, useCageName, useCageTimezone } from '@/contexts/cage';
import { useCageFilter } from '@/contexts/cage-filter-context';
import { usePenCohortFishGroup } from '@/contexts/pen-cohort-context';
import { useCommitImageFeedback } from '@/domain/imageFeedback';
import { useGetWelfareImages } from '@/domain/images';
import { useGetFullWelfare } from '@/domain/welfare/useGetFullWelfare';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { dayStartISOInTimezone } from '@/utils/date';
import { MERGED_WELFARE_IMAGES_TYPE } from '@/utils/welfareImageHelpers';

import CageWelfareMaturationDetectionsPageContent from './CageWelfareMaturationDetectionsPageContent';

function CageWelfareMaturationDetectionsContainer({ cageId }: CagePageInterface) {
    const { t } = useTranslation();
    const { isOptoScaleAdmin } = usePermissions();

    const { loading: cageLoading } = useCage();
    const timezone = useCageTimezone();
    const cageName = useCageName();
    const { normalizedFrom, normalizedTo, daypartTag, selectedFishGroupId } = useCageFilter();
    const from = normalizedFrom ? dayStartISOInTimezone(normalizedFrom, timezone) : undefined;
    const to = normalizedTo ? dayStartISOInTimezone(normalizedTo, timezone) : undefined;

    const onCommitImageFeedback = useCommitImageFeedback({ cageId });

    const { data: fishHealth, isLoading: fishHealthLoading } = useGetFullWelfare(
        cageId,
        { from, to, daypartTag: daypartTag || 'day', fishGroupId: selectedFishGroupId },
        { staleTime: 5 * 60 * 1000 }
    );

    const imagesFishGroup = usePenCohortFishGroup();
    const { data: imagesdata, isLoading: imagesdataLoading } = useGetWelfareImages(
        cageId,
        { type: MERGED_WELFARE_IMAGES_TYPE },
        { emptyWhileLoading: true, keepPreviousData: true, fishGroup: imagesFishGroup }
    );

    const loading = cageLoading || imagesdataLoading || fishHealthLoading;

    return (
        <PageLayout
            title={cageName}
            subtitle={t('cageWelfare.maturationDetections.detectionsMaturation')}>
            {loading && <Spinner size="lg" fullScreen />}
            {!loading && fishHealth && (
                <CageWelfareMaturationDetectionsPageContent
                    fishHealthData={fishHealth.welfareData}
                    imagesData={imagesdataLoading ? [] : (imagesdata ?? [])}
                    isOptoScaleAdmin={isOptoScaleAdmin}
                    onCommitFalseDetection={onCommitImageFeedback}
                    cageId={cageId}
                    timezone={timezone}
                />
            )}
        </PageLayout>
    );
}

export default CageWelfareMaturationDetectionsContainer;
export { CageWelfareMaturationDetectionsContainer };
